import axios from "axios";
import { API_URL } from "@/config";
import { resetStateToDefault } from "@/lib/store/index";
import { queryClient } from "@/lib/queryClient";

const defaultState = Object.freeze({
  options: [],
});

// Sorts array-valued criteria so selection order in a multi-select filter doesn't defeat the
// cache key.
function normalizeCriteriaForCaching(criteria) {
  const normalized = {};

  for (const key of Object.keys(criteria).sort()) {
    const value = criteria[key];

    normalized[key] = Array.isArray(value) ? [...value].sort() : value;
  }

  return normalized;
}

const store = {
  namespaced: true,

  state: {
    ...defaultState,
  },

  getters: {
    canChangeAllergens: (state) => {
      return (dietId) => {
        const diet = state.options.find((option) => option.value === dietId);

        return !!diet?.allowChangingAllergens;
      };
    },

    isDietChangeBlocked: (state) => {
      return (dietId) => {
        const diet = state.options.find((option) => option.value === dietId);

        return !!diet?.blockDietChangeDuringOrdering;
      };
    },
  },

  mutations: {
    setOptions(state, { options }) {
      state.options = options;
    },

    clearOptionsCache(state) {
      state.options = [];
    },

    resetState(state) {
      resetStateToDefault(state, defaultState);
    },
  },

  actions: {
    async getMany(_, criteria = {}) {
      const response = await axios.get(`${API_URL}/diets`, { params: criteria });

      if (!response.data || typeof response.data.items !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async getOne(_, dietId) {
      const response = await axios.get(`${API_URL}/diets/${dietId}`);

      if (!response.data || typeof response.data.id !== "number") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async create(_, dietData) {
      const response = await axios.post(`${API_URL}/diets`, dietData);

      if (response.status !== 201 || !response.data || typeof response.data.id !== "number") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async update(_, { dietId, dietData }) {
      const response = await axios.put(`${API_URL}/diets/${dietId}`, dietData);

      if (response.status !== 200 || !response.data || typeof response.data.id !== "number") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async remove(_, dietId) {
      const response = await axios.delete(`${API_URL}/diets/${dietId}`);

      if (response.status !== 200 || !response.data.status) {
        throw new Error("Error occurred while processing your request.");
      }

      return true;
    },

    async increasePriority(_, dietId) {
      const response = await axios.post(`${API_URL}/diets/${dietId}/increasePriority`);

      if (response.status !== 200 || !response.data.status) {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async decreasePriority(_, dietId) {
      const response = await axios.post(`${API_URL}/diets/${dietId}/decreasePriority`);

      if (response.status !== 200 || !response.data.status) {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async markAsDefault(_, dietId) {
      const response = await axios.post(`${API_URL}/diets/${dietId}/markAsDefault`);

      if (
        response.status !== 200 ||
        typeof response.data.id !== "number" ||
        !response.data.default
      ) {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async getDefaultDiet() {
      const response = await axios.get(`${API_URL}/diets/default`);

      if (!response.data || typeof response.data.id !== "number") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    // Routed through queryClient so every DietsSelect asking for the same criteria shares one
    // request instead of refetching per mount.
    async getOptions({ commit }, criteria = {}) {
      const normalizedCriteria = normalizeCriteriaForCaching(criteria);

      const options = await queryClient.fetchQuery({
        queryKey: ["diets", "options", normalizedCriteria],
        queryFn: async () => {
          const response = await axios.get(`${API_URL}/diets/options`, {
            params: normalizedCriteria,
          });

          if (!response.data || typeof response.data !== "object") {
            throw new Error("Error occurred while processing your request.");
          }

          return response.data;
        },
        staleTime: 5 * 60 * 1000,
      });

      commit("setOptions", { options });

      return options;
    },
  },
};

export default store;
